import { format } from 'date-fns';
import { isValidElement, useContext, useMemo } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import {
    faBan,
    faCalendarCheck,
    faCalendarTimes,
    faCalendarXmark,
    faSnooze,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AnalyticsContext } from 'src/AnalyticsContext';
import { DrawerContext, OnNext } from 'src/components/drawers/DrawerManager';
import { CheckInReasons, CheckInStatuses } from 'src/constants/CheckIn';
import { Drawers } from 'src/constants/Drawers';
import useCheckIns from 'src/hooks/useCheckIns';
import { Unit, useUpdateCheckInMutation } from 'src/store/sageAdminApi';
import { pluralize } from 'src/utils/intlUtils';
import { snackActions } from 'src/utils/snackBarTools';

interface UnitCheckInCardProps {
    unit: Unit;
    onNext?: OnNext;
    hideContainer?: boolean;
    analyticsPrefix?: string;
}

function UnitCheckInCard(props: UnitCheckInCardProps) {
    const { unit, onNext, hideContainer = false, analyticsPrefix } = props;

    const { palette, shape } = useTheme();
    const { trackEvent } = useContext(AnalyticsContext);
    const { openDrawer } = useContext(DrawerContext);

    const { id: unitRid, displayName } = unit || {};

    const {
        checkIns,
        isDuringDowntime,
        isLoading: isLoadingCheckIns,
    } = useCheckIns({ unitId: unitRid });

    const [updateCheckIn, { isLoading: isUpdatingCheckIn }] = useUpdateCheckInMutation();

    const unitCheckIn = useMemo(
        () => checkIns?.find((checkIn) => checkIn.unit?.id === unitRid),
        [checkIns, unitRid]
    );

    const allResidentsHaveOptedOut = unit.currentOccupants?.every(
        (resident) => resident.isCheckInsEnabled === false
    );

    const isCheckedIn = unitCheckIn?.status === CheckInStatuses.Complete;
    const isPendingCheckIn = unitCheckIn?.status === CheckInStatuses.Pending;

    const {
        cardTitle,
        cardSubtitle,
        icon: StatusIcon,
    } = useMemo(() => {
        if (isDuringDowntime) {
            return {
                cardTitle: (
                    <Typography variant="body1">
                        Daily check-in begins at <strong>4:00 AM</strong>
                    </Typography>
                ),
                icon: <FontAwesomeIcon icon={faSnooze} color={palette.common.black} />,
            };
        } else if (allResidentsHaveOptedOut) {
            return {
                cardTitle: (
                    <Typography variant="body1">
                        {pluralize(
                            unit.currentOccupants?.length || 0,
                            'Resident',
                            'Residents',
                            false
                        )}{' '}
                        opted out of check-ins
                    </Typography>
                ),
                icon: <FontAwesomeIcon icon={faBan} color={palette.common.black} />,
            };
        } else if (isPendingCheckIn) {
            return {
                cardTitle: 'Unit not Checked-in today',
                icon: <FontAwesomeIcon icon={faCalendarTimes} color={palette.error.main} />,
            };
        } else if (!unitCheckIn) {
            return {
                cardTitle: 'No unit Check-in available today',
                icon: <FontAwesomeIcon icon={faCalendarTimes} color={palette.error.main} />,
            };
        }

        const checkInTimestamp = unitCheckIn.checkInTimestamp
            ? format(parseInt(unitCheckIn.checkInTimestamp), "MMM d, yyyy 'at' h:mm a")
            : '';

        switch (unitCheckIn?.checkInReason) {
            case CheckInReasons.ButtonPressed:
                return { cardTitle: 'Resident pressed the button', cardSubtitle: checkInTimestamp };
            case CheckInReasons.CalledResident:
                return { cardTitle: 'Called resident', cardSubtitle: checkInTimestamp };
            case CheckInReasons.SeenInPerson:
                return { cardTitle: 'Seen in-person', cardSubtitle: checkInTimestamp };
            case CheckInReasons.TeamMemberVerified:
                return { cardTitle: 'Team member verified', cardSubtitle: checkInTimestamp };
            case CheckInReasons.VerifiedByIntegration:
                return { cardTitle: 'Verified by integration', cardSubtitle: checkInTimestamp };
            case CheckInReasons.MovementSensorDetected:
                return { cardTitle: 'Movement detected', cardSubtitle: checkInTimestamp };
            default:
                return { cardTitle: 'Unit Checked-in', cardSubtitle: checkInTimestamp };
        }
    }, [
        allResidentsHaveOptedOut,
        isDuringDowntime,
        isPendingCheckIn,
        palette,
        unit.currentOccupants?.length,
        unitCheckIn,
    ]);

    const handleCheckInUnit = () => {
        trackEvent('Check-in Unit Button Clicked', { context: analyticsPrefix, unitRid });

        if (onNext) {
            onNext({ unit: { ...unit, checkIn: unitCheckIn } }, { type: Drawers.UnitCheckIn });
        } else {
            openDrawer({
                type: Drawers.UnitCheckIn,
                props: { unit: { ...unit, checkIn: unitCheckIn } },
            });
        }
    };

    const handleRemoveCheckIn = async () => {
        trackEvent('Remove Check-in Button Clicked', { context: analyticsPrefix, unitRid });

        if (!unitCheckIn) return;

        try {
            await updateCheckIn({
                checkInId: unitCheckIn.id!,
                updateCheckInRequest: { status: CheckInStatuses.Pending },
            }).unwrap();

            snackActions.success(`Check-in updated for unit ${displayName}`);
        } catch (error) {
            datadogRum.addError(error, { message: 'Error removing check-in', unitRid });
            snackActions.error('Error updating check in, please try again');
        }
    };

    return (
        <Stack
            sx={{
                gap: 2,
                flex: 1,
                width: '100%',
                ...(!hideContainer && {
                    p: 2,
                    borderRadius: shape.borderRadiusLg,
                    bgcolor: 'background.paper',
                }),
            }}
        >
            {(cardTitle || cardSubtitle) && (
                <Stack sx={{ gap: 0.5, alignItems: 'center' }}>
                    <Stack
                        direction="row"
                        sx={{ alignItems: 'center', justifyContent: 'center', gap: 1 }}
                    >
                        {StatusIcon && StatusIcon}

                        {cardTitle &&
                            (isValidElement(cardTitle) ? (
                                cardTitle
                            ) : (
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {cardTitle}
                                </Typography>
                            ))}
                    </Stack>

                    {cardSubtitle && (
                        <Typography variant="subtitle1" sx={{ color: 'gray.500' }}>
                            {cardSubtitle}
                        </Typography>
                    )}
                </Stack>
            )}

            {!allResidentsHaveOptedOut &&
                !isDuringDowntime &&
                (isLoadingCheckIns ? (
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        height={56}
                        width="100%"
                        sx={{ borderRadius: 3 }}
                    />
                ) : (
                    <>
                        {isPendingCheckIn && (
                            <Button
                                fullWidth
                                disableElevation
                                variant="outlined"
                                endIcon={<FontAwesomeIcon icon={faCalendarCheck} />}
                                color="primary"
                                onClick={handleCheckInUnit}
                                disabled={isDuringDowntime}
                            >
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    Check-in Unit
                                </Typography>
                            </Button>
                        )}

                        {isCheckedIn && (
                            <Button
                                fullWidth
                                disableElevation
                                variant="outlined"
                                endIcon={<FontAwesomeIcon icon={faCalendarXmark} />}
                                color="error"
                                onClick={handleRemoveCheckIn}
                                loading={isUpdatingCheckIn}
                            >
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    Remove Unit Check-in
                                </Typography>
                            </Button>
                        )}
                    </>
                ))}
        </Stack>
    );
}

export default UnitCheckInCard;
